import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0 }
};

const SchoolERPIndia = () => {
  return (
    <div className="bg-gray-50 overflow-hidden">

      {/* SEO */}
      <Helmet>
        <title>Gurukul ERP – Top School ERP Software in India</title>
        <meta name="description" content="Best School ERP Software in India. Manage attendance, fees, exams with Gurukul ERP." />
        
      </Helmet>

      {/* HERO */}
      <section className="relative py-32 bg-gradient-to-br from-purple-900 via-purple-700 to-indigo-800 text-white text-center">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,white,transparent)]"></div>

        <motion.div variants={fadeUp} initial="hidden" animate="show" className="relative max-w-5xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
            Gurukul ERP – Top School ERP Software in India
          </h1>

          <p className="text-lg text-gray-200 mb-8">
            Automate school operations, improve communication, and manage your entire institution from one smart platform.
          </p>

          <div className="flex justify-center gap-4 flex-wrap">
            <Link 
            to="/contact"
            className="bg-yellow-500 px-8 py-3 rounded-full font-semibold hover:scale-110 transition">
              Book Demo
            </Link>
            <Link 
            to="/"
            className="border px-8 py-3 rounded-full hover:bg-white hover:text-black transition">
              Explore Features
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ABOUT WITH IMAGE */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">

          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h2 className="text-4xl font-bold text-purple-900 mb-6">
              Complete Digital School Management
            </h2>
            <p className="text-gray-700 mb-4">
              Gurukul ERP is designed to simplify complex school operations by digitizing
              attendance, fees, exams, and communication into one centralized platform.
            </p>
            <p className="text-gray-600">
              Schools across India use Gurukul ERP to reduce manual work, improve efficiency,
              and deliver better learning experiences.
            </p>
          </motion.div>

          <motion.img
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            src="/herosectionhr.png"
            alt="School ERP Dashboard"
            className="rounded-2xl shadow-xl"
          />

        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-28 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">

          <h2 className="text-4xl font-bold text-center mb-16 text-purple-900">
            Everything Your School Needs
          </h2>

          <div className="grid md:grid-cols-3 gap-10">

            {[
              { title: "Attendance System", desc: "Track attendance in real-time with detailed reports." },
              { title: "Fee Management", desc: "Automate fee collection and financial tracking." },
              { title: "Exam System", desc: "Generate results and performance analysis instantly." },
              { title: "Course Material", desc: "Upload and manage syllabus, notes, PDFs and resources easily." },
              { title: "Teacher Dashboard", desc: "Manage classes, assignments, and reports easily." },
              { title: "Mobile App", desc: "Access everything from anywhere." }
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                className="p-8 bg-white rounded-2xl shadow hover:shadow-2xl hover:-translate-y-2 transition"
              >
                <h3 className="text-xl font-semibold mb-2 text-purple-900">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* BENEFITS WITH IMAGE */}
      <section className="py-28 bg-gradient-to-r from-purple-800 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">

          <motion.img
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            src="/ChatGPT Image Feb 28, 2026, 05_03_03 PM.png"
            alt="School ERP Benefits"
            className="rounded-2xl shadow-xl"
          />

          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h2 className="text-4xl font-bold mb-6">
              Why Gurukul ERP is the Smart Choice
            </h2>
            <ul className="space-y-4 text-lg">
              <li>✔ Reduce administrative workload</li>
              <li>✔ Improve communication</li>
              <li>✔ Real-time insights & reports</li>
              <li>✔ Secure cloud infrastructure</li>
            </ul>
          </motion.div>

        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-purple-900 mb-10">
            Simple Implementation Process
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {["Analysis", "Setup", "Training", "Go Live"].map((step, i) => (
              <div key={i} className="bg-gray-100 p-6 rounded-xl shadow">
                <h3 className="font-semibold text-purple-800">{step}</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Smooth ERP implementation for your school.
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-gradient-to-br from-purple-900 via-purple-700 to-indigo-800 text-white text-center relative overflow-hidden">

  {/* glow effect */}
  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,white,transparent)]"></div>

  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    className="relative max-w-4xl mx-auto px-6"
  >

    <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
      Ready to Digitize Your School?
    </h2>

    <p className="mb-6 text-gray-200 text-lg max-w-2xl mx-auto">
      Join hundreds of institutions across India using Gurukul ERP to simplify operations,
      improve efficiency, and deliver better learning experiences.
    </p>

    <Link
    to="/contact"
      className="bg-yellow-400 text-black px-8 py-3 rounded-full font-semibold hover:scale-110 hover:bg-yellow-300 transition shadow-lg"
    >
      
      Get Free Demo
    </Link>

  </motion.div>
</section>

    </div>
  );
};

export default SchoolERPIndia;